import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  InputAdornment,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Avatar,
  Button,
} from '@mui/material';
import {
  Search,
  Person,
  Inventory,
  Receipt,
  ShoppingCart,
  People,
  MoneyOff,
  Settings,
  AccountBalance,
  Category,
  Refresh,
  FilterList,
} from '@mui/icons-material';
import { subscribeToActivityLogs } from '../firebase/activityLogService';

const ActivityLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAction, setFilterAction] = useState('all');
  const [filterEntity, setFilterEntity] = useState('all');
  const [filterUser, setFilterUser] = useState('all');
  
  // Date filter - default to today
  const getTodayDateString = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  };
  const [dateFilter, setDateFilter] = useState('today');
  const [customStartDate, setCustomStartDate] = useState(getTodayDateString());
  const [customEndDate, setCustomEndDate] = useState(getTodayDateString());

  // Get date range based on filter
  const getDateRange = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const endOfDay = new Date();
    endOfDay.setHours(23, 59, 59, 999);

    switch (dateFilter) {
      case 'today':
        return {
          startDate: today.toISOString(),
          endDate: endOfDay.toISOString()
        };
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const endYesterday = new Date(yesterday);
        endYesterday.setHours(23, 59, 59, 999);
        return {
          startDate: yesterday.toISOString(),
          endDate: endYesterday.toISOString()
        };
      case 'last7days':
        const last7 = new Date(today);
        last7.setDate(last7.getDate() - 7);
        return {
          startDate: last7.toISOString(),
          endDate: endOfDay.toISOString()
        };
      case 'last30days':
        const last30 = new Date(today);
        last30.setDate(last30.getDate() - 30);
        return {
          startDate: last30.toISOString(),
          endDate: endOfDay.toISOString()
        };
      case 'custom':
        const customStart = new Date(customStartDate);
        customStart.setHours(0, 0, 0, 0);
        const customEnd = new Date(customEndDate);
        customEnd.setHours(23, 59, 59, 999);
        return {
          startDate: customStart.toISOString(),
          endDate: customEnd.toISOString()
        };
      case 'all':
        return { startDate: null, endDate: null };
      default:
        return {
          startDate: today.toISOString(),
          endDate: endOfDay.toISOString()
        };
    }
  };

  useEffect(() => {
    const { startDate, endDate } = getDateRange();
    
    setLoading(true);
    
    const unsubscribe = subscribeToActivityLogs((data) => {
      setLogs(data);
      setLoading(false);
      setRefreshing(false);
    }, { 
      limitCount: 500,
      startDate,
      endDate,
      // entityType removed from query — filtered in-memory below to avoid composite index
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilter, customStartDate, customEndDate]);

  // Get unique users for filter
  const uniqueUsers = [...new Set(logs.map(log => log.userEmail))];

  // Filter logs in-memory (search, action, user, entityType)
  const filteredLogs = logs.filter(log => {
    const matchesSearch = 
      log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.entityName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.userEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.userName.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesAction = filterAction === 'all' || log.action.includes(filterAction);
    const matchesUser = filterUser === 'all' || log.userEmail === filterUser;
    const matchesEntity = filterEntity === 'all' || log.entityType === filterEntity;

    return matchesSearch && matchesAction && matchesUser && matchesEntity;
  });

  const handleRefresh = () => {
    setRefreshing(true);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilterAction('all');
    setFilterEntity('all');
    setFilterUser('all');
    setDateFilter('today');
    setCustomStartDate(getTodayDateString());
    setCustomEndDate(getTodayDateString());
  };

  const hasActiveFilters = searchTerm || filterAction !== 'all' || filterEntity !== 'all' || filterUser !== 'all' || dateFilter !== 'today';

  // Get icon for entity type
  const getEntityIcon = (entityType) => {
    const icons = {
      inventory: <Inventory fontSize="small" />,
      grn: <ShoppingCart fontSize="small" />,
      stock_transfer: <ShoppingCart fontSize="small" />,
      purchase_invoice: <ShoppingCart fontSize="small" />,
      purchase_order: <ShoppingCart fontSize="small" />,
      supplier: <People fontSize="small" />,
      trade_payable: <AccountBalance fontSize="small" />,
      trade_receivable: <AccountBalance fontSize="small" />,
      cash_bank: <AccountBalance fontSize="small" />,
      bill: <Receipt fontSize="small" />,
      customer: <People fontSize="small" />,
      expense: <MoneyOff fontSize="small" />,
      expense_category: <Category fontSize="small" />,
      creditor: <AccountBalance fontSize="small" />,
      staff: <Person fontSize="small" />,
      settings: <Settings fontSize="small" />,
      auth: <Person fontSize="small" />,
    };
    return icons[entityType] || <Settings fontSize="small" />;
  };

  // Get color for action type
  const getActionColor = (action) => {
    if (action.includes('created')) return 'success';
    if (action.includes('updated')) return 'info';
    if (action.includes('deleted') || action.includes('archived')) return 'error';
    if (action.includes('login')) return 'primary';
    if (action.includes('logout')) return 'default';
    return 'default';
  };

  // Format action text
  const formatAction = (action) => {
    const labels = {
      created_grn: 'Created GRN',
      updated_grn: 'Updated GRN',
      deleted_grn: 'Deleted GRN',
      created_stock_transfer: 'Created Stock Transfer',
      created_purchase_invoice: 'Created Purchase Invoice',
      updated_purchase_invoice: 'Updated Purchase Invoice',
      deleted_purchase_invoice: 'Deleted Purchase Invoice',
      created_purchase_order: 'Created PO',
      updated_purchase_order: 'Updated PO',
      deleted_purchase_order: 'Deleted PO',
      cancelled_purchase_order: 'Cancelled PO',
      created_supplier: 'Created Supplier',
      updated_supplier: 'Updated Supplier',
      deleted_supplier: 'Deleted Supplier',
      created_trade_payable: 'Created Payable',
      deleted_trade_payable: 'Deleted Payable',
      recorded_payment: 'Recorded Payment',
      cash_credit: 'Cash Credit',
      cash_debit: 'Cash Debit',
      cash_transfer: 'Cash Transfer',
      cash_reconcile: 'Reconciliation',
      initialized_accounts: 'Accounts Initialized',
      deleted_transaction: 'Deleted Transaction',
      stock_adjustment: 'Stock Adjustment',
      created_inventory_item: 'Created Item',
      updated_inventory_item: 'Updated Item',
      deleted_inventory_item: 'Deleted Item',
    };
    return labels[action] || action
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Format entity type
  const formatEntityType = (entityType) => {
    const labels = {
      grn: 'GRN',
      stock_transfer: 'Stock Transfer',
      purchase_invoice: 'Purchase Invoice',
      purchase_order: 'Purchase Order',
      supplier: 'Supplier',
      trade_payable: 'Trade Payable',
      trade_receivable: 'Trade Receivable',
      cash_bank: 'Cash Bank',
      bill: 'Bill',
      inventory: 'Inventory',
      customer: 'Customer',
      expense: 'Expense',
      expense_category: 'Expense Category',
      creditor: 'Creditor',
      staff: 'Staff',
      settings: 'Settings',
      auth: 'Auth',
      return: 'Return',
    };
    return labels[entityType] || entityType
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Format details
  const formatDetails = (details) => {
    if (!details || Object.keys(details).length === 0) return '-';
    return Object.entries(details)
      .map(([key, value]) => `${key}: ${value}`)
      .join(', ');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Activity Logs
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Complete audit trail of all system activities
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={refreshing ? <CircularProgress size={20} /> : <Refresh />}
          onClick={handleRefresh}
          disabled={refreshing}
        >
          Refresh
        </Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <FilterList />
            <Typography variant="h6" fontWeight={600}>
              Filters
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Date Range</InputLabel>
                <Select
                  value={dateFilter}
                  label="Date Range"
                  onChange={(e) => setDateFilter(e.target.value)}
                >
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="yesterday">Yesterday</MenuItem>
                  <MenuItem value="last7days">Last 7 Days</MenuItem>
                  <MenuItem value="last30days">Last 30 Days</MenuItem>
                  <MenuItem value="custom">Custom Range</MenuItem>
                  <MenuItem value="all">All Time (High Reads)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {dateFilter === 'custom' && (
              <>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="End Date"
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} md={dateFilter === 'custom' ? 2 : 3}>
              <FormControl fullWidth>
                <InputLabel>Entity Type</InputLabel>
                <Select
                  value={filterEntity}
                  label="Entity Type"
                  onChange={(e) => setFilterEntity(e.target.value)}
                >
                  <MenuItem value="all">All Entities</MenuItem>
                  <MenuItem value="grn">GRN</MenuItem>
                  <MenuItem value="stock_transfer">Stock Transfer</MenuItem>
                  <MenuItem value="purchase_order">Purchase Order</MenuItem>
                  <MenuItem value="supplier">Supplier</MenuItem>
                  <MenuItem value="trade_payable">Trade Payable</MenuItem>
                  <MenuItem value="trade_receivable">Trade Receivable</MenuItem>
                  <MenuItem value="cash_bank">Cash & Bank</MenuItem>
                  <MenuItem value="inventory">Inventory</MenuItem>
                  <MenuItem value="bill">Bill</MenuItem>
                  <MenuItem value="customer">Customer</MenuItem>
                  <MenuItem value="expense">Expense</MenuItem>
                  <MenuItem value="expense_category">Expense Category</MenuItem>
                  <MenuItem value="creditor">Creditor</MenuItem>
                  <MenuItem value="staff">Staff</MenuItem>
                  <MenuItem value="settings">Settings</MenuItem>
                  <MenuItem value="auth">Authentication</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={dateFilter === 'custom' ? 3 : 3}>
              <FormControl fullWidth>
                <InputLabel>Action Type</InputLabel>
                <Select
                  value={filterAction}
                  label="Action Type"
                  onChange={(e) => setFilterAction(e.target.value)}
                >
                  <MenuItem value="all">All Actions</MenuItem>
                  <MenuItem value="created">Created</MenuItem>
                  <MenuItem value="updated">Updated</MenuItem>
                  <MenuItem value="deleted">Deleted</MenuItem>
                  <MenuItem value="archived">Archived</MenuItem>
                  <MenuItem value="login">Login</MenuItem>
                  <MenuItem value="logout">Logout</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={dateFilter === 'custom' ? 12 : 3}>
              <FormControl fullWidth>
                <InputLabel>User</InputLabel>
                <Select
                  value={filterUser}
                  label="User"
                  onChange={(e) => setFilterUser(e.target.value)}
                >
                  <MenuItem value="all">All Users</MenuItem>
                  {uniqueUsers.map(email => (
                    <MenuItem key={email} value={email}>{email}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                placeholder="Search by action, entity name, user name, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
            {hasActiveFilters && (
              <Button
                size="small"
                variant="outlined"
                onClick={handleClearFilters}
              >
                Clear All Filters
              </Button>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
              Showing {filteredLogs.length} of {logs.length} activities
            </Typography>
            {dateFilter === 'all' && (
              <Chip 
                label="High Reads" 
                size="small" 
                color="warning" 
                sx={{ ml: 1 }}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Entity</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredLogs.map((log) => (
                  <TableRow key={log.id} hover>
                    <TableCell>
                      <Typography variant="body2">
                        {log.timestamp.toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {log.timestamp.toLocaleTimeString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>
                          {log.userName?.charAt(0).toUpperCase() || 'U'}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {log.userName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {log.userEmail}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={formatAction(log.action)}
                        size="small"
                        color={getActionColor(log.action)}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getEntityIcon(log.entityType)}
                        <Typography variant="body2">
                          {formatEntityType(log.entityType)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {log.entityName || '-'}
                      </Typography>
                      {log.entityId && (
                        <Typography variant="caption" color="text.secondary">
                          ID: {log.entityId.substring(0, 8)}...
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {formatDetails(log.details)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredLogs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No activity logs found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ActivityLogs;
